<template>
    <UserMenu
        :items="items"
        :show="show"
        :nickname="authStore.user?.name || ''"
        @click:item="handleClick"
        @open:popper="emits('open:popper')"
        @close:popper="emits('close:popper')"
        @close:manual="emits('close:manual')"
    >
    </UserMenu>
</template>

<script setup lang="ts">
import { type BasicMenuEmits } from '@molecules/BasicMenu/BasicMenu.vue';
import UserMenu from '@molecules/UserMenu/UserMenu.vue';

import { useAuthStore } from '@stores/auth';

import type { ApiFetchCommonError } from '@repository/types';

type UserMenuWrapperProps = {
    openedInModal?: boolean;
    hideMyEvents?: boolean;
    show?: boolean;
};

const props = withDefaults(defineProps<UserMenuWrapperProps>(), {
    openedInModal: false,
    hideMyEvents: true,
    show: undefined,
});

const emits = defineEmits<BasicMenuEmits>();

const { showErrors } = useFetchErrors();
const authStore = useAuthStore();
const { $api } = useNuxtApp();
const { t } = useI18n();

const handleClick = async (name: string) => {
    methodsDict[name as keyof typeof methodsDict] &&
        (await methodsDict[name as keyof typeof methodsDict]());
};

const newWindowOptions = {
    open: {
        target: '_blank',
    },
};

const handleNavigateToDetails = async () => {
    return props.openedInModal
        ? await navigateTo('/user-details', newWindowOptions)
        : await navigateTo('/user-details');
};

const handleNavigateToEvents = async () => {
    return props.openedInModal
        ? await navigateTo('/', newWindowOptions)
        : await navigateTo('/');
};

const handleLogout = async () => {
    try {
        await $api.auth.logout();
        authStore.user = undefined;
        if (!props.openedInModal) await navigateTo('/auth');
        // clearNuxtState is not clearing the state properly so in order
        // to achive the same effect we reload the window
        window.location.reload();
    } catch (error) {
        const errors = (error as ApiFetchCommonError).data?.errors;
        if (errors) showErrors(errors);
    }
};

const methodsDict = {
    logout: handleLogout,
    details: handleNavigateToDetails,
    events: handleNavigateToEvents,
};

const items = computed(() => {
    const items = [
        {
            name: 'avatar',
        },
        {
            name: 'details',
            text: t('atp.components.user_menu.details'),
            icon: 'avatar-simple',
        },
        {
            name: 'events',
            text: t('atp.components.user_menu.my_events'),
            icon: 'list',
        },
        {
            name: 'logout',
            text: t('atp.common.logout'),
            icon: 'shut-down',
            iconClasses: 'fill-error',
        },
    ];
    return props.hideMyEvents
        ? items.filter(({ name }) => name !== 'events')
        : items;
});
</script>
